import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import DataTableElement from "../../../components/elements/DataTableElement";
import { invoicesResource } from "../../../helpers/configRoutes/backend_helper";
import { currencyUSD } from "../../../helpers/forms/formater";

const statusClass = (status) =>
    status === "Paid"
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-amber-50 text-amber-700 border-amber-200";

const AccountSettlement = () => {
    const { id } = useParams();
    const [invoice, setInvoice] = useState(null);
    const [tableLoad, setTableLoad] = useState({ show: false, textLoad: "Loading invoice..." });
    const [processingStatus, setProcessingStatus] = useState(false);

    const loadInvoice = async () => {
        setTableLoad((prev) => ({ ...prev, show: true }));
        const response = await invoicesResource("show", {}, id);
        setInvoice(response?.invoice || null);
        setTableLoad((prev) => ({ ...prev, show: false }));
    };

    useEffect(() => {
        loadInvoice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const updateStatus = async (status) => {
        if (!invoice?.id || invoice.status === status) return;
        setProcessingStatus(true);
        const response = await invoicesResource("put", { status }, invoice.id);
        if (response?.status) {
            setInvoice(response.invoice);
        }
        setProcessingStatus(false);
    };

    const cols = useMemo(() => [
        {
            id: "load_id",
            name: "LOAD",
            selector: (row) => `#${row.load_id}`,
        },
        {
            id: "description",
            name: "DESCRIPTION",
            selector: (row) => row.description || `Load #${row.load_id}`,
        },
        {
            id: "stops",
            name: "STOPS",
            selector: (row) => row.load?.stops?.length || 0,
        },
        {
            id: "amount",
            name: "AMOUNT",
            selector: (row) => currencyUSD(row.amount),
        },
    ], []);

    const invoiceLoads = invoice?.loads || [];

    return (
        <div className="min-h-screen bg-cs-bg-alt p-4 md:p-8">
            <div className="max-full mx-auto space-y-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.18em] text-cs-muted">Accounting center</p>
                        <h1 className="text-2xl font-semibold text-cs-dark">Invoice {invoice?.invoice_number || ""}</h1>
                        <p className="text-sm text-cs-muted mt-1">Review invoice loads, totals and payment status.</p>
                    </div>
                    {invoice && (
                        <select
                            value={invoice.status || "Unpaid"}
                            disabled={processingStatus}
                            onChange={(event) => updateStatus(event.target.value)}
                            className={`border rounded-full px-4 py-2 text-sm font-semibold ${statusClass(invoice.status)}`}
                        >
                            <option value="Unpaid">Unpaid</option>
                            <option value="Paid">Paid</option>
                        </select>
                    )}
                </div>

                <div className="bg-cs-bg rounded-2xl shadow-sm border border-cs-border overflow-hidden">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5 border-b border-cs-border">
                        <div>
                            <h3 className="text-xs font-semibold text-cs-muted uppercase tracking-wide mb-2">Invoice From</h3>
                            <p className="text-sm font-semibold text-cs-dark">M. Sendero</p>
                            <p className="text-xs text-cs-muted">Transport office</p>
                        </div>
                        <div>
                            <h3 className="text-xs font-semibold text-cs-muted uppercase tracking-wide mb-2">Invoice To</h3>
                            <p className="text-sm font-semibold text-cs-dark">{invoice?.client_name || "—"}</p>
                            <p className="text-xs text-cs-muted">Client invoice</p>
                        </div>
                        <div className="md:text-right">
                            <h3 className="text-xs font-semibold text-cs-muted uppercase tracking-wide mb-2">Dates</h3>
                            <p className="text-xs text-cs-muted">Invoice Date: <span className="text-cs-dark">{invoice?.invoice_date || "—"}</span></p>
                            <p className="text-xs text-cs-muted">Due Date: <span className="text-cs-dark">{invoice?.due_date || "—"}</span></p>
                        </div>
                    </div>

                    <DataTableElement columns={cols} items={invoiceLoads} tableLoad={tableLoad} />

                    <div className="flex justify-end p-5 border-t border-cs-border bg-cs-bg-alt/40">
                        <div className="w-full md:w-80 space-y-2 text-sm">
                            <div className="flex justify-between text-cs-muted">
                                <span>Subtotal</span>
                                <span>{currencyUSD(invoice?.subtotal || 0)}</span>
                            </div>
                            <div className="flex justify-between text-cs-muted">
                                <span>Tax</span>
                                <span>{currencyUSD(invoice?.tax || 0)}</span>
                            </div>
                            <div className="flex justify-between text-lg font-semibold text-cs-dark border-t border-cs-border pt-2">
                                <span>Total</span>
                                <span>{currencyUSD(invoice?.total || 0)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountSettlement;
